'use client';

// module imports
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// file imports
import { IQuestion } from '@/types';

interface SortableQuestionItemProps {
  question: IQuestion;
  isSelected: boolean;
  isEnabled: boolean;
  onSelect: () => void;
  onToggle: () => void;
  canEditQuestions: boolean;
  index: number;
}

export default function SortableQuestionItem({ question, isSelected, isEnabled, onSelect, onToggle, canEditQuestions, index }: SortableQuestionItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: question._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className={`p-3 rounded-lg cursor-pointer transition-all duration-200 ${isSelected ? 'bg-blue-50 border-2 border-blue-300' : 'border-2 border-transparent hover:bg-gray-50 hover:border-gray-200'} ${!isEnabled ? 'opacity-60' : ''}`} onClick={onSelect}>
      <div className="flex items-center gap-3">
        <div className={`flex items-center gap-2 ${question.isMovable && canEditQuestions ? '-ml-2' : 'ml-4'}`}>
          {question.isMovable && canEditQuestions && (
            <span {...attributes} {...listeners} className="text-gray-400 cursor-grab active:cursor-grabbing hover:text-gray-600 select-none" onClick={(e) => e.stopPropagation()} title="Drag to reorder">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </span>
          )}
          <span className="text-sm font-medium text-gray-700">
            {index + 1}. {question.label}
          </span>
        </div>
        <div className="ml-auto">
          <button
            type="button"
            className={`cursor-pointer ${isSelected ? 'text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            disabled={!canEditQuestions}
            title={isEnabled ? 'Hide question' : 'Show question'}
          >
            {isEnabled ? (
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
